import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { DetailSection } from "./productDetailContent";

type Props = {
    sections: DetailSection[];
};

const ProductDetailAccordion = ({ sections }: Props) => {
    const [openSections, setOpenSections] = useState<string[]>([
        sections[0]?.title ?? "",
    ]);

    const [expandedText, setExpandedText] = useState<
        Record<string, boolean>
    >({});

    const [openFaqs, setOpenFaqs] = useState<
        Record<string, boolean>
    >({});

    const [activeDetailTabs, setActiveDetailTabs] = useState<
        Record<string, string>
    >({});

    const toggle = (title: string) => {
        setOpenSections((prev) =>
            prev.includes(title)
                ? prev.filter((item) => item !== title)
                : [...prev, title]
        );
    };

    const toggleFaq = (sectionTitle: string, question: string) => {
        const key = `${sectionTitle}-${question}`;

        setOpenFaqs((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <div className="pd-accordion">
            {sections.map((section) => {
                const open = openSections.includes(section.title);

                const longBullets =
                    (section.bullets?.length ?? 0) > 4;

                const expanded =
                    expandedText[section.title] ?? false;

                const visibleBullets = expanded
                    ? section.bullets
                    : section.bullets?.slice(0, 4);

                const activeTabId =
                    activeDetailTabs[section.title] ||
                    section.tabs?.[0]?.id ||
                    "";

                const activeTab =
                    section.tabs?.find(
                        (tab) => tab.id === activeTabId
                    ) ||
                    section.tabs?.[0] ||
                    null;

                return (
                    <article
                        className={`pd-accordion-item ${open ? "open" : ""} ${
                            section.faqs?.length ? "pd-faq-section" : ""
                        } ${
                            section.tabs?.length
                                ? "pd-item-details-section"
                                : ""
                        }`}
                        key={section.title}
                    >
                        <button
                            type="button"
                            className="pd-accordion-trigger"
                            onClick={() => toggle(section.title)}
                            aria-expanded={open}
                        >
                            <span>
                                <strong>{section.title}</strong>
                                <small>{section.subtitle}</small>
                            </span>

                            <ChevronDown size={19} strokeWidth={2} />
                        </button>

                        {open && (
                            <div className="pd-accordion-content">
                                {section.rows?.length ? (
                                    <div className="pd-detail-list">
                                        {section.rows.map((row) => (
                                            <div
                                                className="pd-detail-row"
                                                key={`${section.title}-${row.label}`}
                                            >
                                                <span>{row.label}</span>
                                                <strong>{row.value}</strong>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}

                                {visibleBullets?.length ? (
                                    <ul className="pd-bullets">
                                        {visibleBullets.map((bullet) => (
                                            <li key={bullet}>{bullet}</li>
                                        ))}
                                    </ul>
                                ) : null}

                                {section.paragraph ? (
                                    <p className="pd-detail-paragraph">
                                        {section.paragraph}
                                    </p>
                                ) : null}

                                {section.tabs?.length && activeTab ? (
                                    <div className="pd-all-details">
                                        <div
                                            className="pd-all-details-tabs-scroll"
                                            role="presentation"
                                        >
                                            <div
                                                className="pd-all-details-tabs"
                                                role="tablist"
                                                aria-label="Item details"
                                            >
                                                {section.tabs.map((tab) => {
                                                    const selected =
                                                        tab.id === activeTab.id;

                                                    return (
                                                        <button
                                                            type="button"
                                                            role="tab"
                                                            aria-selected={
                                                                selected
                                                            }
                                                            className={
                                                                selected
                                                                    ? "active"
                                                                    : ""
                                                            }
                                                            key={tab.id}
                                                            onClick={() =>
                                                                setActiveDetailTabs(
                                                                    (
                                                                        previous
                                                                    ) => ({
                                                                        ...previous,
                                                                        [section.title]:
                                                                            tab.id,
                                                                    })
                                                                )
                                                            }
                                                        >
                                                            {tab.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div
                                            className="pd-all-details-panel"
                                            role="tabpanel"
                                            aria-label={activeTab.label}
                                        >
                                            {activeTab.description && (
                                                <p className="pd-all-details-description">
                                                    {activeTab.description}
                                                </p>
                                            )}

                                            <div className="pd-all-details-groups">
                                                {activeTab.groups.map(
                                                    (group) => (
                                                        <section
                                                            className="pd-all-details-group"
                                                            key={`${activeTab.id}-${group.title}`}
                                                        >
                                                            <h3>
                                                                {group.title}
                                                            </h3>

                                                            <div className="pd-all-details-table">
                                                                {group.rows.map(
                                                                    (row) => (
                                                                        <div
                                                                            className="pd-all-details-row"
                                                                            key={`${activeTab.id}-${group.title}-${row.label}`}
                                                                        >
                                                                            <span>
                                                                                {
                                                                                    row.label
                                                                                }
                                                                            </span>

                                                                            <strong>
                                                                                {
                                                                                    row.value
                                                                                }
                                                                            </strong>
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        </section>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {section.faqs?.length ? (
                                    <div className="pd-faq-list">
                                        {section.faqs.map((faq, index) => {
                                            const faqKey = `${section.title}-${faq.question}`;

                                            const faqOpen =
                                                openFaqs[faqKey] ?? false;

                                            return (
                                                <div
                                                    className={`pd-faq-item ${
                                                        faqOpen ? "open" : ""
                                                    }`}
                                                    key={faqKey}
                                                >
                                                    <button
                                                        type="button"
                                                        className="pd-faq-question"
                                                        onClick={() =>
                                                            toggleFaq(
                                                                section.title,
                                                                faq.question
                                                            )
                                                        }
                                                        aria-expanded={faqOpen}
                                                    >
                                                        <span className="pd-faq-number">
                                                            {String(
                                                                index + 1
                                                            ).padStart(2, "0")}
                                                        </span>

                                                        <strong>
                                                            {faq.question}
                                                        </strong>

                                                        <ChevronDown
                                                            size={17}
                                                            strokeWidth={2}
                                                        />
                                                    </button>

                                                    {faqOpen && (
                                                        <div className="pd-faq-answer">
                                                            <span>Answer</span>
                                                            <p>{faq.answer}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}

                                {longBullets && (
                                    <button
                                        type="button"
                                        className="pd-see-more"
                                        onClick={() =>
                                            setExpandedText((prev) => ({
                                                ...prev,
                                                [section.title]: !expanded,
                                            }))
                                        }
                                    >
                                        {expanded ? "Show less" : "See more"}
                                    </button>
                                )}
                            </div>
                        )}
                    </article>
                );
            })}
        </div>
    );
};

export default ProductDetailAccordion;
